import React from 'react';
import { Check, Building2, Layers, Droplets, Sun } from 'lucide-react';

const Pill = ({ children }) => (
  <span className="inline-block text-[10.5px] tracking-[0.18em] font-semibold px-2.5 py-1 border border-current rounded-sm">
    {children}
  </span>
);

const MembraneSystems = () => {
  return (
    <>
      {/* SECTION 1 — EPDM / RESITRIX / HERTALAN (dark) */}
      <section id="epdm" className="bg-[#0b0b0b] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="flex items-center gap-3 mb-10">
            <span className="block w-7 h-[2px] bg-[#e30613]" />
            <h3 className="font-bold tracking-[0.22em] text-[12px] text-white">
              SYSTEM EPDM / RESITRIX / HERTALAN
            </h3>
          </div>

          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-start">
            {/* TEXT */}
            <div>
              <h2 className="text-white font-black uppercase tracking-tight leading-[1.1] text-[26px] md:text-[34px] max-w-[520px]">
                Syntetyczna guma, która pracuje z budynkiem przez dekady.
              </h2>
              <div className="mt-6 flex flex-wrap gap-2 text-[#e30613]">
                <Pill>EPDM</Pill>
                <Pill>HERTALAN (Carlisle)</Pill>
                <Pill>RESITRIX SKW (Carlisle)</Pill>
              </div>

              <div className="mt-7 space-y-5 text-[14.5px] leading-[1.75] text-white/80 max-w-[640px]">
                <p>
                  <span className="text-white font-semibold">EPDM</span> (Ethylene Propylene Diene Monomer)
                  to syntetyczna membrana kauczukowa o wyjątkowej elastyczności i odporności na UV,
                  ozon oraz wahania temperatur. Pracuje z konstrukcją bez pękania nawet przez 40+ lat.
                </p>
                <p>
                  <span className="text-white font-semibold">HERTALAN</span> to holenderski system EPDM
                  z portfolio Carlisle Construction Materials — membrany jednoarkuszowe zamawiane pod
                  wymiar dachu, co radykalnie ogranicza liczbę łączeń i punktów potencjalnych nieszczelności.
                </p>
                <p>
                  <span className="text-white font-semibold">RESITRIX SKW Full Bond</span> (Carlisle) to
                  premium-system EPDM zbrojony włókniną poliestrową z fabrycznie naniesioną warstwą
                  modyfikowanego bitumu samoprzylepnego. Trwale klejony do podłoża — bez balastu,
                  bez mocowania mechanicznego, bez ryzyka podwiania.
                </p>
              </div>

              <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-[640px]">
                {[
                  'Membrany pod wymiar (minimum łączeń)',
                  'Wulkanizowane szwy lub klejenie systemowe',
                  'Pełna wytrzymałość na UV i ozon',
                  'Ekstremalna elastyczność (do −40 °C)',
                  'Kompatybilne z odwodnieniami i attykami',
                  'Trwałość 40+ lat przy poprawnym wykonaniu',
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-[13.5px] text-white/85">
                    <Check size={15} className="text-[#e30613] mt-1 shrink-0" strokeWidth={3} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-9 grid grid-cols-3 gap-4 max-w-[520px]">
                <div className="border-l-2 border-[#e30613] pl-4">
                  <p className="text-[11px] tracking-[0.18em] text-white/55 uppercase">Trwałość</p>
                  <p className="text-[20px] font-bold mt-1">40+ lat</p>
                </div>
                <div className="border-l-2 border-[#e30613] pl-4">
                  <p className="text-[11px] tracking-[0.18em] text-white/55 uppercase">Praca w temp.</p>
                  <p className="text-[20px] font-bold mt-1">−40 / +120</p>
                </div>
                <div className="border-l-2 border-[#e30613] pl-4">
                  <p className="text-[11px] tracking-[0.18em] text-white/55 uppercase">Łączenia</p>
                  <p className="text-[20px] font-bold mt-1">min.</p>
                </div>
              </div>
            </div>

            {/* IMAGES */}
            <div className="grid grid-cols-1 gap-4">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src="https://static.vecteezy.com/system/resources/thumbnails/024/630/396/small/epdm-membrane-installation-using-hot-air-blower-photo.jpeg"
                  alt="Montaż membrany EPDM"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src="https://static.vecteezy.com/system/resources/thumbnails/073/637/466/small/worker-installing-waterproofing-membrane-on-flat-roof-with-water-reflection-professional-roofer-pressing-protective-film-building-renovation-moisture-barrier-rooftop-repair-construction-concept-photo.jpg"
                    alt="Hydroizolacja dachu płaskiego"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1746068012622-bda3402e6e73?w=900&q=80"
                    alt="Detal dachu EPDM"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — TPO / FPO / PVC BAUDER (light) */}
      <section id="tpo" className="bg-[#f3f3f1] text-[#1c1c1c]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="flex items-center gap-3 mb-10">
            <span className="block w-7 h-[2px] bg-[#e30613]" />
            <h3 className="font-bold tracking-[0.22em] text-[12px] text-[#1c1c1c]">
              SYSTEM TPO / FPO / PVC — BAUDER
            </h3>
          </div>

          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-14 items-start">
            {/* IMAGES - real Bauder TPO realization */}
            <div className="grid grid-cols-1 gap-4 lg:order-1 order-2">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_admiring-kepler-5/artifacts/r4zrpf8y_20220711_120340.jpg"
                  alt="Realizacja Bauder TPO — widok dachu"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_admiring-kepler-5/artifacts/vx59opc4_20220711_120342.jpg"
                    alt="Detal attyki — Bauder TPO"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_admiring-kepler-5/artifacts/qp6esw17_20220711_120346.jpg"
                    alt="Powierzchnia membrany Bauder"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_admiring-kepler-5/artifacts/nwtp7dsm_20220711_120404.jpg"
                    alt="Ukończony dach Bauder TPO"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* TEXT */}
            <div className="lg:order-2 order-1">
              <h2 className="text-[#1c1c1c] font-black uppercase tracking-tight leading-[1.1] text-[26px] md:text-[34px] max-w-[600px]">
                Termoplastyczne membrany Bauder dla lekkich dachów przemysłowych i halowych.
              </h2>
              <div className="mt-6 flex flex-wrap gap-2 text-[#1c1c1c]">
                <Pill>BauderTHERMOPLAN-T</Pill>
                <Pill>BauderTHERMOFIN</Pill>
                <Pill>FPO / TPO</Pill>
                <Pill>BAUDER (DE)</Pill>
              </div>

              <div className="mt-7 space-y-5 text-[14.5px] leading-[1.75] text-[#2a2a2a] max-w-[640px]">
                <p>
                  <span className="font-semibold text-[#1c1c1c]">BauderTHERMOPLAN-T</span> to nowoczesny
                  system hydroizolacji <span className="font-semibold text-[#1c1c1c]">FPO</span>,
                  przewyższający parametrami klasyczne folie PVC. Stosowany na lekkich konstrukcjach
                  dachowych — w nowych obiektach, renowacjach i pod dachy zielone. Zgodny z bitumem
                  i izolacją EPS, odporny na przerost korzeni wg wytycznych FLL.
                </p>
                <p>
                  <span className="font-semibold text-[#1c1c1c]">BauderTHERMOFIN</span> to ekologiczna
                  membrana <span className="font-semibold text-[#1c1c1c]">FPO/TPO</span> bez chloru,
                  zmiękczaczy i metali ciężkich, ze zbrojeniem z włókniny szklanej. Zgrzewana w niższych
                  temperaturach niż PVC, charakteryzuje się lepszą odpornością na UV i porównywalną
                  wytrzymałością mechaniczną — wybór szczególnie polecany dla wielkopowierzchniowych
                  obiektów przemysłowych dbających o środowisko.
                </p>
                <p>
                  Łączenia wykonujemy <span className="font-semibold text-[#1c1c1c]">zgrzewaniem
                  gorącym powietrzem</span> (bez dymu i uciążliwego zapachu). Membrany mocowane są
                  mechanicznie lub układane pod balastem (żwir, płyty, zazielenienie). Po latach
                  użytkowania nadają się do pełnego recyklingu materiałowego lub termicznego.
                </p>
              </div>

              <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-[640px]">
                {[
                  'Bez chloru, plastyfikatorów i metali ciężkich (THERMOFIN)',
                  'Zgrzewane szwy gorącym powietrzem',
                  'Zgodność z bitumem (EN 13956)',
                  'Odporność na UV i przerost korzeni (FLL)',
                  'Mocowanie mechaniczne lub balast',
                  'Pełna możliwość recyklingu po eksploatacji',
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-[13.5px] text-[#2a2a2a]">
                    <Check size={15} className="text-[#e30613] mt-1 shrink-0" strokeWidth={3} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-9 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-[640px]">
                <div className="flex items-start gap-3">
                  <Building2 size={22} className="text-[#e30613] mt-0.5 shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="text-[11px] tracking-[0.18em] text-[#666] uppercase">Skala</p>
                    <p className="text-[13px] font-bold mt-0.5">Hale, przemysł</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Layers size={22} className="text-[#e30613] mt-0.5 shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="text-[11px] tracking-[0.18em] text-[#666] uppercase">Łączenia</p>
                    <p className="text-[13px] font-bold mt-0.5">Zgrzewane</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Sun size={22} className="text-[#e30613] mt-0.5 shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="text-[11px] tracking-[0.18em] text-[#666] uppercase">Ekologia</p>
                    <p className="text-[13px] font-bold mt-0.5">Recykling</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Droplets size={22} className="text-[#e30613] mt-0.5 shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="text-[11px] tracking-[0.18em] text-[#666] uppercase">Norma</p>
                    <p className="text-[13px] font-bold mt-0.5">EN 13956</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default MembraneSystems;
